<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div class="editor-dialog">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center py-2 px-0">
          <span class="primary--text">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text class="pa-4">
        <label class="label-input">Section Name</label>
        <v-row>
          <v-col cols="12" md="8">
            <v-text-field v-model="fields.title" label="Name" />
          </v-col>
          <v-col cols="12" md="4">
            <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" />
          </v-col>
        </v-row>

        <label class="label-input">Header</label>
        <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />

        <div class="mt-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <label class="label-input mb-0">Tab reviews</label>
            <v-btn small color="primary" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              Add Review
            </v-btn>
          </div>

          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <ColorPicker
                  v-model="fields.contentBackground"
                  name="leonieandco-review-grid-content"
                  label="Content Background Color"
                  default-color="#fcf8f5"
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  v-model="fields.starColor"
                  name="leonieandco-review-grid-star-color"
                  label="Star Color"
                  default-color="#00B979"
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  v-model="fields.tabHeaderColor"
                  name="leonieandco-review-grid-tab-header-color"
                  label="Tab Header Color"
                  default-color="#772647"
                />
              </v-col>
            </v-row>

            <v-expansion-panels accordion class="mt-4">
              <v-expansion-panel v-for="(item, index) in fields.listItem" :key="index">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center flex-grow-1">
                    <span>{{ item.header || `Review ${index + 1}` }}</span>
                    <v-btn icon @click.stop="handleRemoveItem(index)">
                      <v-icon color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="item.header" label="Tab header" dense />

                  <label class="d-block mb-2">Image</label>
                  <UploadImage
                    :image="{ imageSrc: item.image }"
                    :height="`240`"
                    :typeUpload="'image'"
                    @callback="obj => handleUploadImage(obj, index)"
                    @callbackDelete="handleRemoveImage(index)"
                  />

                  <div class="mt-5 mb-4">
                    <label class="d-block mb-2">Stars (1–5)</label>
                    <v-rating
                      :value="item.rating"
                      :color="fields.starColor"
                      background-color="#d9d9d9"
                      hover
                      length="5"
                      size="28"
                      @input="value => (item.rating = value)"
                    />
                  </div>

                  <div class="mt-5">
                    <label class="d-block mb-2">Content</label>
                    <ckeditor :editor="editor" v-model="item.value" :config="editorConfig" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <SectionSettingsContainerFuild
          class="mt-6"
          :settings="fields.settings"
          :containerPage="fields.containerPage"
          :showBackgroundImage="false"
          :showContainerPage="false"
        />
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions class="py-2 px-0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultImage = 'https://cdn.skyfastimg.com/images/2026/09/21/image-6ab0a806b99c8bf0706e3fda.webp?w=1000';

const defaultItem = (header = 'Customer') => ({
  header,
  image: defaultImage,
  rating: 5,
  value: `
    <p><span style="color:hsl(336,52%,31%);"><strong>Title&nbsp;</strong></span></p>
    <p><span style="font-size:13px;"><i>Content</i></span></p>
  `,
});

export default {
  name: 'LeonieandcoReviewGrid',
  components: { UploadImage, ColorPicker, SectionSettingsContainerFuild },
  props: {
    sectionEditor: { type: Object, required: true },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Leonieandco review grid',
        enableAnchor: false,
        header: '',
        starColor: '#00B979',
        tabHeaderColor: '#772647',
        contentBackground: '#fcf8f5',
        listItem: [],
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#160C2A',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
      },
    };
  },
  mounted() {
    const extract = this.sectionEditor.sectionExtract || {};
    this.fields = {
      ...this.fields,
      ...extract,
      settings: { ...this.fields.settings, ...(extract.settings || {}) },
      containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
      listItem: (extract.listItem || []).map(item => ({ ...defaultItem(), ...item })),
    };
  },
  methods: {
    handleAddItem() {
      this.fields.listItem.push(defaultItem(`Customer ${this.fields.listItem.length + 1}`));
    },
    handleRemoveItem(index) {
      this.fields.listItem.splice(index, 1);
    },
    handleUploadImage(obj, index) {
      this.fields.listItem[index].image = obj.imageSrc;
    },
    handleRemoveImage(index) {
      this.fields.listItem[index].image = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionLeonieandcoReviewGrid(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-dialog {
  background-color: #fff;
}

::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}

.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px;
  font-weight: 500;
}

.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}

.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}

.header-modal {
  top: 0;
}

.footer-modal {
  bottom: 0;
}
</style>
